import React, { useMemo } from "react";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RecordVoiceOverOutlinedIcon from "@mui/icons-material/RecordVoiceOverOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";

const unique = (items) => Array.from(new Set((items || []).filter(Boolean)));

const AiChatVoiceConfigPanel = ({
  value,
  onChange,
  subscribers = [],
  selectedSubscriberId = "all",
  onSelectedSubscriberIdChange,
}) => {
  const voices = Array.isArray(value?.voices) ? value.voices : [];
  const voiceValues = useMemo(() => new Set(voices.map((voice) => String(voice?.value || ""))), [voices]);
  const globalAllowed = unique(value?.allowedVoices).filter((voice) => voiceValues.has(voice));
  const subscriberAllowed = value?.subscriberAllowedVoices && typeof value.subscriberAllowedVoices === "object"
    ? value.subscriberAllowedVoices
    : {};
  const subscriberKey = selectedSubscriberId === "all" ? "" : String(selectedSubscriberId || "");
  const hasSubscriberOverride = Boolean(subscriberKey && Array.isArray(subscriberAllowed[subscriberKey]));
  const effectiveAllowed = subscriberKey && hasSubscriberOverride
    ? unique(subscriberAllowed[subscriberKey]).filter((voice) => voiceValues.has(voice))
    : globalAllowed;
  const selectedSubscriber = subscribers.find((subscriber) => String(subscriber.id) === subscriberKey);

  const setGlobalAllowed = (nextAllowed) => {
    const allowedVoices = unique(nextAllowed).filter((voice) => voiceValues.has(voice));
    if (!allowedVoices.length) return;
    const nextDefault = allowedVoices.includes(value?.defaultVoice)
      ? value.defaultVoice
      : allowedVoices[0];
    onChange({ ...value, allowedVoices, defaultVoice: nextDefault });
  };

  const setSubscriberAllowed = (nextAllowed) => {
    if (!subscriberKey) return;
    const allowed = unique(nextAllowed).filter((voice) => voiceValues.has(voice));
    if (!allowed.length) return;
    onChange({
      ...value,
      subscriberAllowedVoices: {
        ...subscriberAllowed,
        [subscriberKey]: allowed,
      },
    });
  };

  const toggleVisibleVoice = (voiceValue) => {
    const current = new Set(effectiveAllowed);
    if (current.has(voiceValue)) {
      if (current.size <= 1) return;
      current.delete(voiceValue);
    } else {
      current.add(voiceValue);
    }
    if (subscriberKey) setSubscriberAllowed(Array.from(current));
    else setGlobalAllowed(Array.from(current));
  };

  const customizeSubscriber = () => {
    if (!subscriberKey) return;
    const base = globalAllowed.length ? globalAllowed : voices.slice(0, 1).map((voice) => voice.value);
    setSubscriberAllowed(base);
  };

  const useGlobalForSubscriber = () => {
    if (!subscriberKey) return;
    const nextOverrides = { ...subscriberAllowed };
    delete nextOverrides[subscriberKey];
    onChange({ ...value, subscriberAllowedVoices: nextOverrides });
  };

  const updateVoice = (index, patch) => {
    const previous = voices[index] || {};
    const previousValue = String(previous.value || "").trim().toLowerCase();
    const nextValue = patch.value !== undefined
      ? String(patch.value || "").trim().toLowerCase()
      : previousValue;
    const nextVoices = voices.map((voice, voiceIndex) =>
      voiceIndex === index ? { ...voice, ...patch, ...(patch.value !== undefined ? { value: nextValue } : {}) } : voice
    );

    if (!previousValue || nextValue === previousValue) {
      onChange({ ...value, voices: nextVoices });
      return;
    }

    const replaceValue = (items) => unique((items || []).map((item) => item === previousValue ? nextValue : item));
    const nextSubscriberAllowed = Object.entries(subscriberAllowed).reduce((acc, [key, items]) => {
      acc[key] = replaceValue(items);
      return acc;
    }, {});
    onChange({
      ...value,
      voices: nextVoices,
      allowedVoices: replaceValue(globalAllowed),
      subscriberAllowedVoices: nextSubscriberAllowed,
      defaultVoice: value?.defaultVoice === previousValue ? nextValue : value?.defaultVoice,
    });
  };

  const addVoice = () => {
    let suffix = voices.length + 1;
    let candidate = `custom-${suffix}`;
    const used = new Set(voices.map((voice) => String(voice?.value || "").toLowerCase()));
    while (used.has(candidate)) {
      suffix += 1;
      candidate = `custom-${suffix}`;
    }
    // New voices enter the catalog only. The super explicitly decides whether
    // they are exposed globally or to a subscriber.
    onChange({
      ...value,
      voices: [...voices, { value: candidate, label: `Custom ${suffix}`, category: "custom" }],
    });
  };

  const deleteVoice = (index) => {
    const removed = voices[index];
    const removedValue = String(removed?.value || "");
    const nextVoices = voices.filter((_, voiceIndex) => voiceIndex !== index);
    if (!nextVoices.length) return;
    let nextGlobalAllowed = globalAllowed.filter((item) => item !== removedValue);
    if (!nextGlobalAllowed.length) nextGlobalAllowed = [nextVoices[0].value];
    const nextSubscriberAllowed = Object.entries(subscriberAllowed).reduce((acc, [key, items]) => {
      const filtered = unique(items).filter((item) => item !== removedValue && nextVoices.some((voice) => voice.value === item));
      if (filtered.length) acc[key] = filtered;
      return acc;
    }, {});
    const nextDefault = nextGlobalAllowed.includes(value?.defaultVoice)
      ? value.defaultVoice
      : nextGlobalAllowed[0];
    onChange({
      ...value,
      voices: nextVoices,
      allowedVoices: nextGlobalAllowed,
      subscriberAllowedVoices: nextSubscriberAllowed,
      defaultVoice: nextDefault,
    });
  };

  return (
    <>
      <section className="system-config-panel">
        <div className="system-config-panel-head">
          <div>
            <h3>AI chat voice replies</h3>
            <p>
              The AI returns text and audio for both <code>/rag/chat</code> and <code>/rag/chat/voice</code>.
              Users choose their own reply voice from the voices you expose to their subscriber.
            </p>
          </div>
        </div>

        <div className="system-config-voice-hero">
          <label className="system-config-switch-card">
            <span>
              <strong>Enable audio replies</strong>
              <small>Send voice_reply=true with text chat and voice chat requests.</small>
            </span>
            <input
              type="checkbox"
              checked={value?.voiceReply !== false}
              onChange={(event) => onChange({ ...value, voiceReply: event.target.checked })}
            />
          </label>

          <label className="system-config-switch-card">
            <span>
              <strong>Fallback voice</strong>
              <small>Used only until a user chooses an available voice of their own.</small>
            </span>
            <select
              value={value?.defaultVoice || globalAllowed[0] || ""}
              onChange={(event) => onChange({ ...value, defaultVoice: event.target.value })}
              disabled={!globalAllowed.length}
            >
              {voices.filter((voice) => globalAllowed.includes(voice.value)).map((voice) => (
                <option key={voice.value} value={voice.value}>{voice.label || voice.value}</option>
              ))}
            </select>
          </label>
        </div>
      </section>

      <section className="system-config-panel">
        <div className="system-config-panel-head">
          <div>
            <h3>Voice visibility</h3>
            <p>Choose which catalog voices users can see for all subscribers or override one subscriber.</p>
          </div>
        </div>

        <div className="system-config-voice-scope-bar">
          <label>
            <span>Apply visibility to</span>
            <select
              value={selectedSubscriberId}
              onChange={(event) => onSelectedSubscriberIdChange?.(event.target.value)}
            >
              <option value="all">All subscribers</option>
              {subscribers.map((subscriber) => (
                <option key={subscriber.id} value={String(subscriber.id)}>
                  {subscriber.name || subscriber.company_name || subscriber.email || `Subscriber ${subscriber.id}`}
                </option>
              ))}
            </select>
          </label>
          {subscriberKey ? (
            <div className="system-config-voice-scope-status">
              <GroupsOutlinedIcon fontSize="small" />
              <span>
                {selectedSubscriber?.name || selectedSubscriber?.company_name || `Subscriber ${subscriberKey}`}: {hasSubscriberOverride ? "custom visibility" : "inherits All subscribers"}
              </span>
              {hasSubscriberOverride ? (
                <button type="button" onClick={useGlobalForSubscriber}>Use global</button>
              ) : (
                <button type="button" onClick={customizeSubscriber}>Customize</button>
              )}
            </div>
          ) : null}
        </div>

        <div className={`system-config-voice-visibility-grid ${subscriberKey && !hasSubscriberOverride ? "is-inherited" : ""}`}>
          {voices.map((voice) => {
            const checked = effectiveAllowed.includes(voice.value);
            return (
              <label key={voice.value} className="system-config-voice-visibility-card">
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={Boolean(subscriberKey && !hasSubscriberOverride)}
                  onChange={() => toggleVisibleVoice(voice.value)}
                />
                <span>
                  <strong>{voice.label || voice.value}</strong>
                  <small>{voice.value} · {voice.category || "custom"}</small>
                </span>
              </label>
            );
          })}
        </div>
      </section>

      <section className="system-config-panel">
        <div className="system-config-panel-head">
          <div>
            <h3>AI voice catalog</h3>
            <p>Add future AI voice names here without a frontend release. Adding a voice does not expose it until you select it above.</p>
          </div>
          <button type="button" className="system-config-add-btn" onClick={addVoice}>
            <AddIcon /> Add voice
          </button>
        </div>

        <div className="system-config-voice-actions">
          {voices.map((voice, index) => (
            <div className="system-config-voice-row" key={`${voice.value || "voice"}-${index}`}>
              <div className="system-config-voice-row-title">
                <RecordVoiceOverOutlinedIcon />
                <strong>{voice.label || voice.value || `Voice ${index + 1}`}</strong>
                <button
                  type="button"
                  className="system-config-icon-btn"
                  onClick={() => deleteVoice(index)}
                  aria-label="Delete AI voice"
                  disabled={voices.length <= 1}
                >
                  <DeleteOutlineIcon />
                </button>
              </div>

              <div className="system-config-editor-grid voice">
                <label>
                  <span>Voice value</span>
                  <input
                    value={voice.value || ""}
                    onChange={(event) => updateVoice(index, { value: event.target.value })}
                    placeholder="nova"
                  />
                </label>
                <label>
                  <span>Display name</span>
                  <input
                    value={voice.label || ""}
                    onChange={(event) => updateVoice(index, { label: event.target.value })}
                    placeholder="Nova"
                  />
                </label>
                <label>
                  <span>Category</span>
                  <input
                    value={voice.category || ""}
                    onChange={(event) => updateVoice(index, { category: event.target.value.toLowerCase() })}
                    placeholder="female / male / neutral / custom"
                  />
                </label>
              </div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
};

export default AiChatVoiceConfigPanel;
